import React, { useMemo, useState } from 'react';

import Papa from 'papaparse';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import DebugModal from '~/components/DebugModal/DebugModal';
import OrderIdLinkCompressed from '~/components/Vouchers/OrderIdLinkCompressed';

import { filterDropDownList } from '~/consts/orderFilter';
import { PROMO_DEVICE_TYPES } from '~/consts/promo';

import { useGetOrdersByIds } from '~/hooks/apis/orders/useGetOrdersByIds';
import { useBulkVoucherOrderAutoAssignByOrderFilters } from '~/hooks/apis/promo/useBulkVoucherOrderAutoAssignByOrderFilters';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import type {
  BulkVoucherOrderAutoAssignByOrderFiltersPayload,
  BulkVoucherOrderAutoAssignByOrderFiltersResponse,
} from '~/services/PromoService';
import { formatDateLongMonthWithMeridiem } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';
import dateFormatterDetailed from '~/utils/dateFormatterDetails';

type Props = {
  onApplyOrder?: (orderId: string) => Promise<void> | void;
  applyingOrderId?: string | null;
  canAssign?: boolean;
};

type AssignmentRow = {
  id: string;
  orderId: string;
  orderCreatedAt: string;
  orderTotal: string;
  evaluation: 'passed' | 'failed' | 'error';
  dryRunSuccess: boolean;
  matchedRules: number;
  reasons: string;
  firstFailedConditionReason: string;
  mode: string;
  canApply: boolean;
  debugData: unknown;
  conditionOutcomes: Array<AssignmentConditionOutcome>;
};

type AssignmentConditionOutcome = {
  id: string;
  conditionType: string;
  passed: boolean;
  errorCodes: Array<string>;
  skipReason?: string;
  ruleId?: string;
  debugData: unknown;
};

const ORDER_STATUS_OPTIONS = (filterDropDownList.status ?? []).map((option) => option.key);
const ORDER_ID_CSV_HEADERS = [
  'order_id',
  'orderId',
  'fk_order_id',
  'id_order',
  'orderid',
  'order_bk',
  'orderBk',
  'fk_order_id',
  'fk_orders',
];
const DEFAULT_PLATFORM_TYPE_OPTIONS = PROMO_DEVICE_TYPES.map((option) => option.key).filter((key) => key !== 'all');
const BULK_UPLOAD_PREVIEW_LIMIT = 10;
const STATUS_LOOKUP_MAX_ORDER_IDS = 50;

function normalizeOrderIds(values: Array<string>): Array<string> {
  return Array.from(new Set(values.map((value) => String(value).trim()).filter(Boolean)));
}

function parseOrderIdsFromCsvFile(file: File): Promise<Array<string>> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string> | Array<string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = results.errors ?? [];
        if (errors.length > 0) {
          reject(new Error(errors[0].message));
          return;
        }

        const headerFields = (results.meta.fields ?? []).map((field) => field.trim());
        const matchingHeaders = headerFields.filter((field) => ORDER_ID_CSV_HEADERS.includes(field));

        if (matchingHeaders.length > 1) {
          reject(new Error(`CSV has multiple matching order ID headers: ${matchingHeaders.join(', ')}`));
          return;
        }

        if (headerFields.length > 1 && matchingHeaders.length === 0) {
          reject(
            new Error(
              `CSV has multiple columns but no matching order ID header. Expected one of: ${ORDER_ID_CSV_HEADERS.join(
                ', ',
              )}
              
              Got headers: ${headerFields.join(', ')}
              `,
            ),
          );
          return;
        }

        if (matchingHeaders.length === 1) {
          const [matchingHeader] = matchingHeaders;
          const ids = (results.data as Array<Record<string, string>>)
            .map((row) => row?.[matchingHeader] ?? '')
            .map((value) => String(value).trim())
            .filter(Boolean);
          resolve(normalizeOrderIds(ids));
          return;
        }

        const idsFromHeaderParse = (results.data as Array<Record<string, string>>)
          .map((row) => Object.values(row)[0] ?? '')
          .map((value) => String(value).trim())
          .filter(Boolean);
        resolve(normalizeOrderIds(idsFromHeaderParse));
      },
      error: (error) => reject(error),
    });
  });
}

function buildRows(response: BulkVoucherOrderAutoAssignByOrderFiltersResponse | undefined): Array<AssignmentRow> {
  const assignments = response?.result.assignments ?? [];
  return assignments.map((assignment, index) => {
    if ('errorCode' in assignment) {
      return {
        id: `${assignment.orderId}-${index}`,
        orderId: assignment.orderId,
        orderCreatedAt: '',
        orderTotal: '',
        evaluation: 'error',
        dryRunSuccess: false,
        matchedRules: 0,
        reasons: `${assignment.errorCode}: ${assignment.message}`,
        firstFailedConditionReason: '',
        mode: 'error',
        canApply: false,
        debugData: assignment,
        conditionOutcomes: [],
      };
    }

    const matchedRules = assignment.outcomes.filter((outcome) => outcome.matched).length;
    const conditionOutcomes = assignment.outcomes.flatMap((outcome, outcomeIndex) =>
      (outcome.conditionOutcomes ?? []).map((condition, conditionIndex) => ({
        id: `${assignment.orderId}-${outcomeIndex}-${conditionIndex}`,
        conditionType: condition.condition_type ?? 'condition',
        passed: Boolean(condition.passed),
        errorCodes: condition.error_codes ?? [],
        skipReason: outcome.skipReason,
        ruleId: outcome.voucherAutoAssignmentRuleId,
        debugData: condition,
      })),
    );
    const failedReasons = assignment.outcomes
      .flatMap((outcome) => {
        if (outcome.matched) {
          return [];
        }
        const conditionReasons =
          outcome.conditionOutcomes
            ?.filter((condition) => !condition.passed)
            .map((condition) => {
              const conditionType = condition.condition_type ?? 'condition';
              const errorCodes = condition.error_codes?.length ? ` (${condition.error_codes.join(', ')})` : '';
              return `${conditionType}${errorCodes}`;
            }) ?? [];

        return [outcome.skipReason, ...conditionReasons].filter((value): value is string => Boolean(value?.trim()));
      })
      .filter((value): value is string => Boolean(value));
    const uniqueFailedReasons = Array.from(new Set(failedReasons));
    const firstFailedConditionReason = uniqueFailedReasons[0] ?? '';

    const evaluation: AssignmentRow['evaluation'] = matchedRules > 0 ? 'passed' : 'failed';
    const order = assignment.order as Record<string, unknown> | undefined;
    const orderCreatedAt = String(order?.createdAt ?? order?.created_at ?? '');
    const rawTotal = order?.total ?? order?.totalAmount ?? order?.orderTotal ?? order?.grandTotal;
    const currencyCode = String(order?.currencyCode ?? order?.currency_code ?? order?.currency ?? 'AUD');
    const numericTotal = typeof rawTotal === 'number' ? rawTotal : Number(rawTotal);
    const orderTotal = Number.isFinite(numericTotal) ? currencyFormatter(currencyCode, numericTotal) : '';

    return {
      id: `${assignment.orderId}-${index}`,
      orderId: assignment.orderId,
      orderCreatedAt,
      orderTotal,
      evaluation,
      dryRunSuccess: evaluation === 'passed',
      matchedRules,
      reasons: uniqueFailedReasons.join('; '),
      firstFailedConditionReason,
      mode: assignment.mode,
      canApply: matchedRules > 0 && assignment.mode !== 'active',
      debugData: assignment,
      conditionOutcomes,
    };
  });
}

export default function VoucherOrderAutoAssignByOrderFiltersTable({
  onApplyOrder,
  applyingOrderId,
  canAssign = true,
}: Props) {
  const [statusValues, setStatusValues] = useState<Array<string>>(['completed']);
  const [customerId, setCustomerId] = useState('');
  const [singleOrderIdInput, setSingleOrderIdInput] = useState('');
  const [selectedUploadedOrderIds, setSelectedUploadedOrderIds] = useState<Array<string>>([]);
  const [isBulkUploadDialogOpen, setIsBulkUploadDialogOpen] = useState(false);
  const [bulkUploadFileName, setBulkUploadFileName] = useState('');
  const [bulkUploadStatus, setBulkUploadStatus] = useState<string | null>(null);
  const [showAllDisplayOrderIds, setShowAllDisplayOrderIds] = useState(false);
  const [bulkUploadDisplayOrderIds, setBulkUploadDisplayOrderIds] = useState<Array<string>>([]);
  const [bulkUploadError, setBulkUploadError] = useState<string | null>(null);
  const [parsedUploadedOrderIds, setParsedUploadedOrderIds] = useState<Array<string>>([]);
  const [platformTypeValues, setPlatformTypeValues] = useState<Array<string>>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [selectedConditionOutcome, setSelectedConditionOutcome] = useState<AssignmentConditionOutcome | null>(null);
  const brand = useCurrentBrand() ?? '';

  const searchMutation = useBulkVoucherOrderAutoAssignByOrderFilters();
  const displayedOrderIdsForStatusLookup = useMemo(
    () => bulkUploadDisplayOrderIds.slice(0, STATUS_LOOKUP_MAX_ORDER_IDS),
    [bulkUploadDisplayOrderIds],
  );
  const displayOrdersStatusQuery = useGetOrdersByIds(
    displayedOrderIdsForStatusLookup,
    brand,
    isBulkUploadDialogOpen && displayedOrderIdsForStatusLookup.length > 0,
  );
  const hasCappedStatusLookup = bulkUploadDisplayOrderIds.length > STATUS_LOOKUP_MAX_ORDER_IDS;
  const displayOrdersById = useMemo(() => {
    const entries = (displayOrdersStatusQuery.data ?? []) as Array<Record<string, unknown>>;
    return new Map(
      entries.map((order) => [
        String(order.id_orders ?? order.id ?? '').trim(),
        {
          status: String(order.status ?? order.order_status ?? '').trim(),
        },
      ]),
    );
  }, [displayOrdersStatusQuery.data]);
  const rows = useMemo(() => buildRows(searchMutation.data), [searchMutation.data]);
  const successfulDryRunRows = useMemo(() => rows.filter((row) => row.dryRunSuccess && row.canApply), [rows]);
  const orderCoverage = useMemo(() => {
    const result = searchMutation.data?.result as Record<string, unknown> | undefined;
    const minDatePlacedFromResult = String(result?.minDatePlaced ?? result?.min_date_placed ?? '').trim();
    const maxDatePlacedFromResult = String(result?.maxDatePlaced ?? result?.max_date_placed ?? '').trim();

    if (minDatePlacedFromResult && maxDatePlacedFromResult) {
      return {
        minDatePlaced: minDatePlacedFromResult,
        maxDatePlaced: maxDatePlacedFromResult,
      };
    }

    const createdAtValues = (searchMutation.data?.result.assignments ?? [])
      .flatMap((assignment) => {
        if ('errorCode' in assignment) {
          return [];
        }
        const createdAt = String(assignment.order?.createdAt ?? '').trim();
        return createdAt ? [createdAt] : [];
      })
      .filter((value) => !Number.isNaN(Date.parse(value)));

    if (createdAtValues.length === 0) {
      return {
        minDatePlaced: minDatePlacedFromResult,
        maxDatePlaced: maxDatePlacedFromResult,
      };
    }

    const sortedCreatedAtValues = [...createdAtValues].sort((a, b) => Date.parse(a) - Date.parse(b));

    return {
      minDatePlaced: minDatePlacedFromResult || sortedCreatedAtValues[0] || '',
      maxDatePlaced: maxDatePlacedFromResult || sortedCreatedAtValues[sortedCreatedAtValues.length - 1] || '',
    };
  }, [searchMutation.data]);
  const platformTypeOptions = useMemo(() => [...DEFAULT_PLATFORM_TYPE_OPTIONS], []);

  const formatCoverageDateTime = (date: string) => formatDateLongMonthWithMeridiem(date);
  const trimmedOrderId = singleOrderIdInput.trim();
  const trimmedCustomerId = customerId.trim();
  const currentPage = Math.max(1, page);
  const currentPerPage = Math.max(1, perPage);
  const orderSvcTotal = searchMutation.data?.result.orderSvcTotal ?? 0;
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = currentPage * currentPerPage < orderSvcTotal;

  const columns = useMemo<Array<GridColDef<AssignmentRow>>>(() => {
    const getRowApplyInfo = (row: AssignmentRow): { disabled: boolean; title: string } => {
      if (!canAssign) {
        return { disabled: true, title: 'Requires promo-admin permission to assign voucher auto-assign rules' };
      }
      if (!onApplyOrder) {
        return { disabled: true, title: 'No apply function provided' };
      }
      if (isBulkAssigning) {
        return { disabled: true, title: 'Bulk assignment in progress' };
      }
      if (applyingOrderId === row.orderId) {
        return { disabled: true, title: `Order ${row.orderId} is being applied` };
      }
      if (!row.canApply) {
        if (row.mode === 'active') {
          return { disabled: true, title: 'Already assigned (active mode result)' };
        }
        if (!row.dryRunSuccess) {
          return { disabled: true, title: row.firstFailedConditionReason || 'Dry-run did not pass for this order' };
        }
        return { disabled: true, title: 'This order cannot be applied from current results' };
      }
      return { disabled: false, title: `Apply voucher auto-assign to order ${row.orderId}` };
    };

    const allColumns: Array<GridColDef<AssignmentRow>> = [
      {
        field: 'orderId',
        headerName: 'Order ID',
        flex: 1,
        maxWidth: 50,
        renderCell: (params) => <OrderIdLinkCompressed orderId={params.row.orderId} />,
      },
      {
        field: 'orderCreatedAt',
        headerName: 'Created',
        minWidth: 180,
        renderCell: (params) => dateFormatterDetailed({ date: params.row.orderCreatedAt }),
      },
      { field: 'orderTotal', headerName: 'Total', minWidth: 140 },
      {
        field: 'dryRunResult',
        headerName: 'Dry-run result',
        minWidth: 150,
        sortable: false,
        renderCell: (params) =>
          params.row.dryRunSuccess ? (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'success.main' }}>
              <CheckCircleOutlinedIcon fontSize="small" />
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'error.main' }}>
              <CancelOutlinedIcon fontSize="small" />
              {params.row.firstFailedConditionReason ? (
                <Typography variant="body2" color="error.main" sx={{ wordBreak: 'break-word' }}>
                  {params.row.firstFailedConditionReason}
                </Typography>
              ) : null}
            </Stack>
          ),
      },
      {
        field: 'reasons',
        headerName: 'Condition Results',
        minWidth: 420,
        flex: 2,
        sortable: false,
        renderCell: (params) => {
          const outcomes = params.row.conditionOutcomes;
          if (outcomes.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                {params.row.reasons || 'No failed reasons'}
              </Typography>
            );
          }

          return (
            <Stack spacing={0.5} sx={{ py: 1 }}>
              {outcomes.map((outcome) => (
                <Box
                  key={outcome.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    columnGap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    {outcome.passed ? (
                      <CheckCircleOutlinedIcon sx={{ color: 'success.main', fontSize: 16 }} />
                    ) : (
                      <CancelOutlinedIcon sx={{ color: 'error.main', fontSize: 16 }} />
                    )}
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {outcome.conditionType}
                      {outcome.errorCodes.length > 0 ? ` (${outcome.errorCodes.join(', ')})` : ''}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    variant="text"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedConditionOutcome(outcome);
                    }}
                  >
                    Debug
                  </Button>
                </Box>
              ))}
            </Stack>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 140,
        sortable: false,
        renderCell: (params) => {
          const applyInfo = getRowApplyInfo(params.row);
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!onApplyOrder) {
                    return;
                  }
                  void onApplyOrder(params.row.orderId);
                }}
                disabled={applyInfo.disabled}
                title={applyInfo.title}
              >
                {applyingOrderId === params.row.orderId ? 'Applying...' : 'Apply'}
              </Button>
              {applyInfo.disabled ? (
                <Tooltip title={applyInfo.title}>
                  <IconButton size="small" aria-label="why apply is disabled">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          );
        },
      },
      {
        field: 'debug',
        headerName: 'Debug',
        minWidth: 140,
        sortable: false,
        renderCell: (params) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <DebugModal type="generic" title={`Order ${params.row.orderId} debug`} data={params.row.debugData} />
          </Box>
        ),
      },
    ];

    return allColumns;
  }, [applyingOrderId, canAssign, isBulkAssigning, onApplyOrder]);

  const getBulkAssignInfo = (): { disabled: boolean; title: string } => {
    if (!canAssign) {
      return { disabled: true, title: 'Requires promo-admin permission to assign voucher auto-assign rules' };
    }

    if (isBulkAssigning) {
      return { disabled: true, title: 'Bulk assignment in progress' };
    }

    if (applyingOrderId) {
      return { disabled: true, title: `Order ${applyingOrderId} is being applied` };
    }

    if (!onApplyOrder) {
      return { disabled: true, title: 'No apply function provided' };
    }

    if (!successfulDryRunRows.length) {
      return { disabled: true, title: 'No successful dry-run results to apply' };
    }

    return { disabled: false, title: `Apply ${successfulDryRunRows.length} orders` };
  };
  const bulkAssignInfo = getBulkAssignInfo();

  const runSearch = (pageOverride?: number) => {
    const parsedSingleOrderId = trimmedOrderId;
    const parsedOrderIds = selectedUploadedOrderIds.length > 0 ? selectedUploadedOrderIds : [];
    const pageToUse = Math.max(1, pageOverride ?? currentPage);
    const perPageToUse = currentPerPage;

    const payload: BulkVoucherOrderAutoAssignByOrderFiltersPayload = {
      dryRun: true,
      orderFilters: {
        page: pageToUse,
        perPage: perPageToUse,
        ...(statusValues.length > 0 ? { status: statusValues.join(',') } : {}),
        ...(trimmedCustomerId ? { customerId: trimmedCustomerId } : {}),
        ...(brand.trim() ? { brand: brand.trim() } : {}),
        ...(parsedSingleOrderId ? { orderIds: [parsedSingleOrderId] } : {}),
        ...(parsedOrderIds.length ? { orderIds: parsedOrderIds } : {}),
        ...(platformTypeValues.length > 0 ? { platformTypes: platformTypeValues.join(',') } : {}),
      },
    };

    searchMutation.mutate(payload);
  };

  const handleSearch = () => {
    runSearch();
  };

  const handlePreviousPage = () => {
    if (!hasPreviousPage || searchMutation.isPending) {
      return;
    }
    const previousPage = currentPage - 1;
    setPage(previousPage);
    runSearch(previousPage);
  };

  const handleNextPage = () => {
    if (!hasNextPage || searchMutation.isPending) {
      return;
    }
    const nextPage = currentPage + 1;
    setPage(nextPage);
    runSearch(nextPage);
  };

  const handleAssignSuccessfulDryRuns = async () => {
    if (!onApplyOrder || successfulDryRunRows.length === 0) {
      return;
    }
    setIsBulkAssigning(true);
    try {
      for (const row of successfulDryRunRows) {
        // Keep assignments deterministic and avoid race conditions with server-side rule evaluation.
        await onApplyOrder(row.orderId);
      }
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleBulkUploadFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setBulkUploadError(null);
    setBulkUploadStatus('Parsing file...');
    setBulkUploadFileName(file.name);

    try {
      const parsedIds = await parseOrderIdsFromCsvFile(file);
      setParsedUploadedOrderIds(parsedIds);
      setBulkUploadStatus(`Parsed ${parsedIds.length} unique order IDs.`);
      if (showAllDisplayOrderIds) {
        setBulkUploadDisplayOrderIds(parsedIds);
      } else {
        setBulkUploadDisplayOrderIds(parsedIds.slice(0, BULK_UPLOAD_PREVIEW_LIMIT));
      }
    } catch (error) {
      setParsedUploadedOrderIds([]);
      setBulkUploadStatus(null);
      setBulkUploadError(`Failed to parse file: ${(error as Error).message}`);
    } finally {
      event.target.value = '';
    }
  };

  const handleUseBulkUploadOrderIds = () => {
    setSelectedUploadedOrderIds(parsedUploadedOrderIds);
    setIsBulkUploadDialogOpen(false);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-end">
        <TextField
          label="Order ID"
          size="small"
          value={singleOrderIdInput}
          onChange={(e) => setSingleOrderIdInput(e.target.value)}
          sx={{ minWidth: 30 }}
          helperText={selectedUploadedOrderIds.length > 0 ? 'Ignored while bulk order IDs are selected.' : undefined}
        />
        <Button variant="text" onClick={() => setIsBulkUploadDialogOpen(true)}>
          Bulk upload orderIds
        </Button>
        {selectedUploadedOrderIds.length > 0 ? (
          <Chip
            label={`${selectedUploadedOrderIds.length} bulk order IDs selected`}
            onDelete={() => setSelectedUploadedOrderIds([])}
          />
        ) : null}
        <Autocomplete
          multiple
          size="small"
          options={ORDER_STATUS_OPTIONS}
          value={statusValues}
          onChange={(_, value) => setStatusValues(value)}
          sx={{ minWidth: 260 }}
          renderInput={(params) => <TextField {...params} label="Status" placeholder="Select status" />}
        />
        <TextField
          label="Customer ID"
          size="small"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <Autocomplete
          multiple
          size="small"
          options={platformTypeOptions}
          value={platformTypeValues}
          onChange={(_, value) => setPlatformTypeValues(value)}
          sx={{ minWidth: 300 }}
          renderInput={(params) => <TextField {...params} label="Platform types" placeholder="Select platform types" />}
        />
        <TextField
          label="Page"
          size="small"
          value={page}
          type="number"
          onChange={(e) => setPage(Math.max(1, Number(e.target.value) || 1))}
          sx={{ width: 90 }}
        />
        <TextField
          label="Per page"
          size="small"
          value={perPage}
          type="number"
          onChange={(e) => setPerPage(Math.max(1, Number(e.target.value) || 25))}
          sx={{ width: 110 }}
        />
        <Button variant="contained" color="primary" onClick={handleSearch} disabled={searchMutation.isPending}>
          Search orders
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => void handleAssignSuccessfulDryRuns()}
          disabled={bulkAssignInfo.disabled}
          title={bulkAssignInfo.title}
        >
          {isBulkAssigning ? 'Assigning...' : `Assign (${successfulDryRunRows.length})`}
        </Button>
        {bulkAssignInfo.disabled ? (
          <Tooltip title={bulkAssignInfo.title}>
            <IconButton size="small" aria-label="why assign is disabled">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>

      {searchMutation.isError && (
        <Alert severity="error">
          {searchMutation.error instanceof Error ? searchMutation.error.message : 'Search failed'}
        </Alert>
      )}

      {searchMutation.data && (
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {searchMutation.data.result.orderSvcTotal} Orders
          </Typography>
          <Stack spacing={0.25}>
            <Typography variant="body2" color="text.secondary">
              <strong>From:</strong>{' '}
              {orderCoverage.minDatePlaced ? formatCoverageDateTime(orderCoverage.minDatePlaced) : 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>To:</strong>{' '}
              {orderCoverage.maxDatePlaced ? formatCoverageDateTime(orderCoverage.maxDatePlaced) : 'N/A'}
            </Typography>
          </Stack>
        </Stack>
      )}

      <Box sx={{ height: '300vh' }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button variant="text" onClick={handlePreviousPage} disabled={!hasPreviousPage || searchMutation.isPending}>
            Previous page
          </Button>
          <Button variant="contained" onClick={handleNextPage} disabled={!hasNextPage || searchMutation.isPending}>
            Next page
          </Button>
        </Stack>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={searchMutation.isPending}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
        />
      </Box>

      <Dialog
        open={Boolean(selectedConditionOutcome)}
        onClose={() => setSelectedConditionOutcome(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedConditionOutcome?.conditionType ?? 'Condition'} debug</DialogTitle>
        <DialogContent>
          {selectedConditionOutcome ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {selectedConditionOutcome.passed ? (
                  <CheckCircleOutlinedIcon sx={{ color: 'success.main' }} />
                ) : (
                  <CancelOutlinedIcon sx={{ color: 'error.main' }} />
                )}
                <Typography variant="body2">Result: {selectedConditionOutcome.passed ? 'Passed' : 'Failed'}</Typography>
              </Stack>
              {selectedConditionOutcome.ruleId ? (
                <Typography variant="body2">Rule ID: {selectedConditionOutcome.ruleId}</Typography>
              ) : null}
              {selectedConditionOutcome.skipReason ? (
                <Typography variant="body2">Skip reason: {selectedConditionOutcome.skipReason}</Typography>
              ) : null}
              <Box
                component="pre"
                sx={{ m: 0, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}
              >
                {JSON.stringify(selectedConditionOutcome.debugData, null, 2)}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedConditionOutcome(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isBulkUploadDialogOpen} onClose={() => setIsBulkUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk upload orderIds</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Bulk upload will scan for columns with the header of:
              </Typography>
              <Stack>
                <Typography variant="body2">- order_id,</Typography>
                <Typography variant="body2">- orderId,</Typography>
                <Typography variant="body2">- fk_order_id,</Typography>
                <Typography variant="body2">- id_order, or</Typography>
                <Typography variant="body2">- orderid</Typography>
              </Stack>
            </Paper>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                component="label"
                variant="contained"
                startIcon={parsedUploadedOrderIds.length > 0 ? <CheckBoxIcon /> : <CloudUploadIcon />}
              >
                {bulkUploadFileName ? 'Replace CSV file' : 'Choose CSV file'}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => void handleBulkUploadFileSelected(event)}
                  style={{
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    height: 1,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    width: 1,
                  }}
                />
              </Button>
              {bulkUploadFileName ? (
                <Typography variant="body2" color="text.secondary">
                  {bulkUploadFileName}
                </Typography>
              ) : null}
            </Stack>
            {bulkUploadStatus ? <Alert severity="success">{bulkUploadStatus}</Alert> : null}
            {bulkUploadDisplayOrderIds.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {showAllDisplayOrderIds
                    ? `Displaying all ${bulkUploadDisplayOrderIds.length} order IDs:`
                    : `Displaying first ${bulkUploadDisplayOrderIds.length} order IDs:`}
                </Typography>
                <Stack>
                  {bulkUploadDisplayOrderIds.map((orderId) => (
                    <Box key={orderId} sx={{ py: 0.25 }}>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <OrderIdLinkCompressed orderId={orderId} />
                        {displayOrdersStatusQuery.isPending ? (
                          <Typography variant="caption" color="text.secondary">
                            Checking status...
                          </Typography>
                        ) : null}
                        {!displayOrdersStatusQuery.isPending && displayOrdersById.get(orderId)?.status ? (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'success.main' }}>
                            <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption" color="text.secondary">
                              {displayOrdersById.get(orderId)?.status}
                            </Typography>
                          </Stack>
                        ) : null}
                        {!displayOrdersStatusQuery.isPending && !displayOrdersById.get(orderId)?.status ? (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'error.main' }}>
                            <CancelOutlinedIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption" color="text.secondary">
                              Not found
                            </Typography>
                          </Stack>
                        ) : null}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                {displayOrdersStatusQuery.isError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {displayOrdersStatusQuery.error instanceof Error
                      ? displayOrdersStatusQuery.error.message
                      : 'Failed to fetch order statuses'}
                  </Alert>
                ) : null}
                {hasCappedStatusLookup ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Showing statuses for the first {STATUS_LOOKUP_MAX_ORDER_IDS} order IDs only.
                  </Alert>
                ) : null}
                {parsedUploadedOrderIds.length > BULK_UPLOAD_PREVIEW_LIMIT ? (
                  <Button
                    variant="text"
                    sx={{ mt: 1 }}
                    onClick={() => {
                      const nextShowAll = !showAllDisplayOrderIds;
                      setShowAllDisplayOrderIds(nextShowAll);
                      setBulkUploadDisplayOrderIds(
                        nextShowAll
                          ? parsedUploadedOrderIds
                          : parsedUploadedOrderIds.slice(0, BULK_UPLOAD_PREVIEW_LIMIT),
                      );
                    }}
                  >
                    {showAllDisplayOrderIds
                      ? `Show first ${BULK_UPLOAD_PREVIEW_LIMIT} order IDs`
                      : `Show all ${parsedUploadedOrderIds.length}`}
                  </Button>
                ) : null}
              </Paper>
            )}
            {bulkUploadError ? <Alert severity="error">{bulkUploadError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBulkUploadDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUseBulkUploadOrderIds}
            disabled={parsedUploadedOrderIds.length === 0}
          >
            Apply OrderId filter ({parsedUploadedOrderIds.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
